export type AuthScope = 'admin' | 'sub' | 'public';

export const ADMIN_TOKEN_STORAGE_KEY = 'token';
export const SUB_TOKEN_STORAGE_KEY = 'sub_token';
export const ADMIN_LOGIN_PATH = '/user/login';
export const SUB_LOGIN_PATH = '/sub/login';

const hasWindow = (): boolean => typeof window !== 'undefined';

export const isAdminLoginPath = (pathname: string): boolean => pathname === ADMIN_LOGIN_PATH;

export const isSubLoginPath = (pathname: string): boolean => pathname === SUB_LOGIN_PATH;

export const isPublicPath = (pathname: string): boolean =>
  isAdminLoginPath(pathname) || isSubLoginPath(pathname);

export const isSubPath = (pathname: string): boolean => pathname === '/sub' || pathname.startsWith('/sub/');

export const resolveRouteAuthScope = (pathname: string): AuthScope => {
  if (isPublicPath(pathname)) {
    return 'public';
  }

  return isSubPath(pathname) ? 'sub' : 'admin';
};

export const resolveApiAuthScope = (url: string): Exclude<AuthScope, 'public'> => {
  if (url.includes('/api/sub-auth') || url.includes('/api/sub/')) {
    return 'sub';
  }

  return 'admin';
};

export const getTokenStorageKey = (scope: Exclude<AuthScope, 'public'>): string =>
  scope === 'sub' ? SUB_TOKEN_STORAGE_KEY : ADMIN_TOKEN_STORAGE_KEY;

export const getStoredToken = (scope: Exclude<AuthScope, 'public'>): string | null => {
  if (!hasWindow()) {
    return null;
  }

  return localStorage.getItem(getTokenStorageKey(scope));
};

const getOtherScope = (scope: Exclude<AuthScope, 'public'>): Exclude<AuthScope, 'public'> =>
  scope === 'sub' ? 'admin' : 'sub';

export const setStoredToken = (scope: Exclude<AuthScope, 'public'>, token: string): void => {
  if (!hasWindow()) {
    return;
  }

  localStorage.setItem(getTokenStorageKey(scope), token);
};

export const activateScopedSession = (
  scope: Exclude<AuthScope, 'public'>,
  token: string,
): void => {
  if (!hasWindow()) {
    return;
  }

  localStorage.removeItem(getTokenStorageKey(getOtherScope(scope)));
  localStorage.setItem(getTokenStorageKey(scope), token);
};

export const clearStoredToken = (scope: Exclude<AuthScope, 'public'>): void => {
  if (!hasWindow()) {
    return;
  }

  localStorage.removeItem(getTokenStorageKey(scope));
};

export const clearOtherScopedToken = (
  scope: Exclude<AuthScope, 'public'>,
): void => {
  if (!hasWindow()) {
    return;
  }

  localStorage.removeItem(getTokenStorageKey(getOtherScope(scope)));
};

export const clearAllStoredTokens = (): void => {
  if (!hasWindow()) {
    return;
  }

  localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
  localStorage.removeItem(SUB_TOKEN_STORAGE_KEY);
};

export const getLoginPath = (scope: Exclude<AuthScope, 'public'>): string =>
  scope === 'sub' ? SUB_LOGIN_PATH : ADMIN_LOGIN_PATH;

export const resolvePostLoginRedirect = (
  scope: Exclude<AuthScope, 'public'>,
  candidate: string | null,
): string => {
  const fallback = scope === 'sub' ? '/sub/accounts' : '/dashboard';

  if (!candidate || candidate.trim() === '') {
    return fallback;
  }

  if (scope === 'sub') {
    return isSubPath(candidate) ? candidate : fallback;
  }

  return isSubPath(candidate) || isPublicPath(candidate) ? fallback : candidate;
};
